import axios, { AxiosError } from 'axios';
import { Storage, getLocalStorage, getCookieStorage, CookieStorage, COOKIE_PREFIX_KEY } from '../storage';
import { Subject, Observable, ReplaySubject } from 'rxjs';
import qs from 'query-string';
import { Logger } from '../logging';
import { createLoggerObject } from './utils';
import globalWindow from '@shared/core/globals';

const FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS = 60;
const BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS = 1000; // 16.67 minutes

const SSR_TOKEN_KEY = 'SsrAuthToken';
const LOCAL_STORAGE_TOKEN_KEY = 'secrets';
export const SSR_TOKEN_COOKIE = COOKIE_PREFIX_KEY + SSR_TOKEN_KEY;

import { executeWithAuth0Retry } from './auth0.retry';
import { windowExists } from '@shared/utils/windowExists';

export interface AccessToken
  extends Readonly<{
    id_token: string;
    bearer_type: string;
    expires_in: number;
  }> {}

interface Secrets {
  accessToken?: AccessToken;
  accessTokenClientId?: string;
  offset?: number;
  /** Required when requesting a new Access Token w/o re-authenticating the user. */
  refreshToken?: string;
  refreshTokenClientId?: string;
}

interface PersistedSecrets extends Pick<Secrets, 'accessTokenClientId' | 'offset' | 'refreshToken' | 'refreshTokenClientId'> {
  accessToken?: string;
}

export interface DecodedToken
  extends Readonly<{
    aud: string;
    azp: string;
    exp: number;
    iat: number;
    iss: string;
    sub: string;
    acr?: string;
    amr?: string[];
  }> {}

const category = 'joy-web.Auth';

const initializeTokenLogger = createLoggerObject({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refreshTokenError: (msg: string, error: unknown, extra: any = {}) => ({
    category,
    action: 'RefreshToken',
    actionType: 'error',
    extraInfo: { message: msg, error, ...extra },
  }),
  ensureSecretsFormatError: (error: unknown) => ({
    category,
    action: 'EnsureSecretsFormat',
    actionType: 'error',
    extraInfo: { error },
  }),
  refreshTokenFirstAttempt: () => ({
    category,
    action: 'RefreshTokenRetry',
  }),
});

/**
 * TokenService manages authentication secrets (access tokens, refresh tokens, client IDs, clock offset).
 *
 * ## Storage Strategy: Cookie-Primary with localStorage Sync
 *
 * **Primary Storage:** Cookies (`joySsrAuthToken`)
 * - Single source of truth for auth secrets
 * - Enables SSR to authenticate GraphQL requests
 * - Supports token refresh on both client and server
 * - Enhanced security with `SameSite=Strict` flag
 *
 * **Secondary Storage:** localStorage (`secrets`) - TEMPORARY
 * - Maintained for backwards compatibility during migration period
 * - All writes go to BOTH storages (dual-write pattern)
 * - Reconciliation logic migrates incomplete cookies from localStorage
 * - TODO: Remove after 1-2 months of production confidence
 *
 * ## Why Cookie-Primary?
 *
 * **Industry Best Practice:**
 * Cookies are the gold standard for storing authentication tokens because:
 * - Native SSR support (automatically sent with HTTP requests)
 * - Security flags (`HttpOnly`, `Secure`, `SameSite`) protect against XSS/CSRF
 * - Browser handles expiration and cleanup automatically
 * - Works seamlessly across client and server contexts
 *
 * **Before (localStorage-primary):**
 * - localStorage was primary, cookies only written after token refresh
 * - Cookies contained ONLY AccessToken (insufficient for client-side operations)
 * - SSR required manual sync between localStorage ↔ cookies
 * - Sync could drift, causing SSR issues
 *
 * **After (cookie-primary):**
 * - Cookies contain FULL Secrets object (refreshToken, clientIds, offset, accessToken)
 * - Eliminates sync issues between storages
 * - SSR always has latest auth state
 * - Enables cookies as single source of truth
 *
 * ## Migration & Reconciliation
 *
 * During the transition period, users may have:
 * - **Partial cookies:** Legacy cookies with only AccessToken (missing refreshToken/clientIds)
 * - **Complete localStorage:** Full secrets in localStorage from before migration
 *
 * **Reconciliation Strategy (Option B - Prefer Complete Secrets):**
 * 1. Read from cookies first (primary storage)
 * 2. If cookie is incomplete, check localStorage for complete secrets
 * 3. If localStorage has complete secrets, migrate them to cookies
 * 4. Continue with complete secrets (seamless for users)
 *
 * **Why No Token Verification Needed:**
 * The reconciliation logic does NOT verify that `id_token` matches between storages because
 * the legacy architecture made token mismatches architecturally impossible:
 * - Cookies only written during token refresh (using same token as localStorage)
 * - Logout cleared both storages atomically
 * - Single controlled login flow via auth callback
 *
 * See "Why Token Mismatch Was Impossible" section in AUTH_TOKEN_FLOW.md for detailed analysis.
 *
 * This ensures users with partial cookies can still refresh tokens and stay logged in.
 *
 * ## Cleanup (Phase 2)
 *
 * After 1-2 months of production confidence:
 * - Remove localStorage dual-write in `writeToSecretsAndAlertListeners()`
 * - Remove localStorage sync in `deleteSecrets()`
 * - Remove reconciliation logic in `collectSavedSecrets()`
 * - Remove `localStorage` field from this class
 * - Cookies become the single source of truth
 *
 * @see {@link doc/guest-site-perf-audit/02-auth-secrets-as-cookie/AUTH_TOKEN_FLOW.md} for detailed migration documentation
 */
export class TokenService {
  private logger: ReturnType<typeof initializeTokenLogger>;
  private readonly accessTokenSubject: Subject<AccessToken | null>;
  private generateAccessTokenPromise: Promise<AccessToken | null> | null = null;
  private refreshTimeoutId: number | undefined;
  private secrets: Secrets = {};
  private readonly storage: Storage;
  private readonly localStorage: Storage | undefined;

  constructor(logger: Logger) {
    this.logger = initializeTokenLogger(logger);
    this.accessTokenSubject = new ReplaySubject(1);
    // Cookie-primary storage for SSR consistency and security
    this.storage = getCookieStorage() || getLocalStorage();
    // TODO: Remove localStorage sync after migration confidence (1-2 months post-deployment)
    this.localStorage = getLocalStorage();
    this.authenticate();
  }

  public accessTokenObservable(): Observable<AccessToken | null> {
    return this.accessTokenSubject;
  }

  public authenticate(): void {
    this.checkTokenExpirationAndQueueRefresh();
  }

  public getToken(): string | null {
    // collect secrets to ensure the in-memory token matches the one in the storage
    // COMMENTED OUT: Seems like this may be causing a regression in createwedding
    // this.collectSavedSecrets();
    return this.getAccessToken();
  }

  public getTokenAsync = async (): Promise<string | null> => {
    // checkTokenExpirationAndQueueRefresh already collects secrets to ensure the in-memory token matches the one in the storage
    await this.checkTokenExpirationAndQueueRefresh();
    return this.getAccessToken();
  };

  public setToken(token: AccessToken | null, clientId: string, refreshToken?: string, serverTime?: number) {
    this.writeToSecretsAndAlertListeners(token, clientId, refreshToken, serverTime);
    this.clearRefreshTimeoutId();
    if (token) {
      this.checkTokenExpirationAndQueueRefresh();
    }
  }

  public deleteSecrets() {
    // Delete from primary storage (cookies)
    this.deleteSecretsFromStorage();

    // Sync deletion to localStorage for backwards compatibility
    // TODO: Remove after migration confidence (1-2 months post-deployment)
    if (this.localStorage) {
      this.localStorage.setItem('secrets', undefined);
    }

    this.clearRefreshTimeoutId();
  }

  public hasValidMfaClaim() {
    const idToken = this.getDecodedAccessToken();

    if (idToken == null) {
      return false;
    }

    const { iat, amr } = idToken;
    const fifteenMinutes = 900;

    const hasTokenMFAClaim = Boolean(amr?.includes('mfa'));
    const now = Math.floor(Date.now() / 1000);
    const isIssuedLessThan15Minutes = iat > now - fifteenMinutes;

    return hasTokenMFAClaim && isIssuedLessThan15Minutes;
  }

  private attemptToRefreshTokenAndWrite = async (): Promise<AccessToken | null> => {
    // If there isn't a promise, create and set it
    if (!this.generateAccessTokenPromise) {
      this.clearRefreshTimeoutId();
      // eslint-disable-next-line no-async-promise-executor
      this.generateAccessTokenPromise = new Promise<AccessToken | null>(async resolve => {
        let newAccessToken: AccessToken | null = this.secrets.accessToken || null;
        // Abort if missing the required API arguments.
        if (this.secrets.accessTokenClientId && this.secrets.refreshToken) {
          const searchParams = qs.stringify({
            client_id: this.secrets.accessTokenClientId,
            grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
            target: this.secrets.accessTokenClientId,
            scope: 'openid',
            api_type: 'app',
            refresh_token: this.secrets.refreshToken,
          });

          try {
            const res = await executeWithAuth0Retry(
              () => axios.post<AccessToken | null>('https://withjoy.auth0.com/delegation', searchParams),
              currentAttempt => {
                if (currentAttempt === 1) {
                  this.logger.refreshTokenFirstAttempt();
                }
              },
            );
            newAccessToken = res.data;
          } catch (err) {
            if ((err as AxiosError).response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              // Will want to check for retry headers
              // console.log(err.response.headers);
              this.logger.refreshTokenError('Server responded, but is unable to refresh the token', err);
            } else if ((err as AxiosError).request) {
              // The request was made but no response was received
              // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
              // http.ClientRequest in node.js
              // console.log(err.request);
              this.logger.refreshTokenError('Request was made but no response was received', err);
            } else {
              // Something happened in setting up the request that triggered an Error
              this.logger.refreshTokenError('Unable to make request', err);
            }

            // Unable to refresh - have them re auth
            newAccessToken = null;
            this.secrets.refreshToken = undefined;
          }
        }

        this.writeToSecretsAndAlertListeners(newAccessToken);
        resolve(newAccessToken);
      }).finally(() => {
        // Remove the promise and begin a new refresh cycle.
        this.generateAccessTokenPromise = null;
        if (this.secrets.accessTokenClientId && this.secrets.refreshToken) {
          this.checkTokenExpirationAndQueueRefresh();
        }
      });
    }

    // This problem will always resolve with either:
    // 1. the new access token
    // 2. the current token or null
    return this.generateAccessTokenPromise;
  };

  private checkTokenExpirationAndQueueRefresh = async (): Promise<AccessToken | null> => {
    // always grab the latest secrets from storage
    this.collectSavedSecrets();

    const decodedToken = this.getDecodedAccessToken();

    if (!decodedToken) {
      return this.attemptToRefreshTokenAndWrite();
    }

    const timeLeft = this.getTimeLeftInSecondsBeforeExpiration(decodedToken, this.secrets.offset);

    // token is expired
    // applying a FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS buffer to help ensure the token is not expired when used
    if (timeLeft <= FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS) {
      // Token has effectively expired - refresh immediately
      return this.attemptToRefreshTokenAndWrite();
    }

    // token is about to expire in BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS (default 16.67 minutes)
    if (timeLeft < BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS) {
      // Token is about to expire soon - refresh immediately, but return the current token
      this.attemptToRefreshTokenAndWrite();
      // token is still approximately valid (has more than FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS left)
      return Promise.resolve(this.secrets.accessToken || null);
    }

    this.clearRefreshTimeoutId();
    this.refreshTimeoutId = globalWindow.setTimeout?.(
      () => {
        this.attemptToRefreshTokenAndWrite();
      },
      (timeLeft - BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS) * 1000,
    );

    return Promise.resolve(this.secrets.accessToken || null);
  };

  private broadcastTokenUpdate(nextToken: AccessToken | null): void {
    this.accessTokenSubject.next(nextToken);
    // this will ensure that subscribers receive only last known token and will not receive updates to avoid re-renderings
    this.accessTokenSubject.complete();
  }

  private clearRefreshTimeoutId() {
    clearTimeout(this.refreshTimeoutId);
    this.refreshTimeoutId = 0;
  }

  /**
   * Helper to check if secrets object has all required fields for full functionality.
   * Complete secrets must have: accessToken, refreshToken, and clientIds.
   *
   * @param secrets - Parsed secrets object to validate
   * @returns true if secrets contain all required fields
   */
  private hasCompleteSecrets(secrets: Secrets): boolean {
    return !!(secrets.accessToken && secrets.refreshToken && secrets.accessTokenClientId);
  }

  /**
   * Collects auth secrets from storage with migration and reconciliation logic.
   *
   * **Priority Order:**
   * 1. Reads from primary storage (cookies)
   * 2. Falls back to localStorage if cookie secrets are incomplete
   * 3. Migrates complete localStorage secrets to cookies
   *
   * **Reconciliation Logic (TODO: Remove after migration confidence):**
   * - If cookie is missing or incomplete (lacks refreshToken/clientIds), checks localStorage
   * - If localStorage has complete secrets (refreshToken + clientIds), migrates them to cookies
   * - Does NOT validate accessToken freshness for migration (refresh flow handles expired tokens)
   * - This ensures users with partial cookies can still refresh tokens and stay logged in
   *
   * **Hash-Based Login:**
   * - Supports hash-based login flow (JOY-8012) for Firefox iframe restrictions
   * - Extracts secrets from URL hash when present
   */
  private collectSavedSecrets(): void {
    // Read from primary storage (cookies)
    let storedSecrets = this.getSecretsFromStorage();

    // Migration & reconciliation logic
    // TODO: Remove after migration confidence (1-2 months post-deployment)
    if (this.localStorage) {
      const cookieSecrets = storedSecrets ? this.parseSecretsStringToJSON(storedSecrets) : null;
      const isCookieIncomplete = !cookieSecrets || !this.hasCompleteSecrets(cookieSecrets);

      // If cookie is missing or incomplete, check localStorage
      if (isCookieIncomplete) {
        const localSecrets = this.localStorage.getItem('secrets');
        if (localSecrets) {
          const parsedLocalSecrets = this.parseSecretsStringToJSON(localSecrets);

          // Prefer localStorage if it has complete secrets (refreshToken + clientIds)
          // Even if accessToken is stale, the refresh flow will handle it using refreshToken
          // This ensures users with expired tokens but valid refreshToken can stay logged in
          if (this.hasCompleteSecrets(parsedLocalSecrets)) {
            storedSecrets = localSecrets;
            // Migrate complete secrets to cookies for future reads
            this.setSecretsToStorage(localSecrets);
          }
        }
      }
    }

    const hash = windowExists() ? qs.parse(window.location.hash) : null;
    const hashSecrets = hash?.secrets;

    // This is to support the hash based login to fix ticket https://withjoy.atlassian.net/browse/JOY-8012
    // in addition to getSecretsForHashBasedLogin.
    if (hashSecrets) {
      this.secrets = this.parseSecretsStringToJSON(hashSecrets as string);
      if (!this.secrets.accessToken) {
        // We don't have a token in the hash, so something bad must have happened
        this.logger.refreshTokenError('Missing access token from hash based login', null);
      }

      // Remove the hash from the url and save the secrets to the storage
      delete hash.secrets;
      window.location.hash = qs.stringify(hash);
      history.replaceState('', document.title, window.location.pathname + window.location.search + window.location.hash);
      const serializedSecrets = this.prepareSecretsForWrite();
      this.setSecretsToStorage(serializedSecrets);
      // Sync to localStorage for backwards compatibility
      if (this.localStorage) {
        this.localStorage.setItem('secrets', serializedSecrets);
      }
    }

    if (storedSecrets) {
      this.secrets = this.parseSecretsStringToJSON(storedSecrets);
    }

    if (!this.secrets) {
      this.secrets = {};
    }
  }

  /**
   * This method is being created to support the hash based login to fix ticket https://withjoy.atlassian.net/browse/JOY-8012
   * where Firefox users are not being able to use the add to joy snippet. Since the snippet is loaded inside an iframe,
   * due to new restrictions in Firefox, the cookies are not being read by this iframe. So we'll be sending the secrets
   * to the app using the hash in the url. The app will read the hash and use the secrets to login the user.
   * @returns a string that can be used to login to the app with a hash (empty string if there are no secrets)
   */
  public getSecretsForHashBasedLogin(): string {
    // Check if secrets object has any meaningful content (accessToken is the key field)
    return this.secrets?.accessToken ? '#secrets=' + encodeURIComponent(JSON.stringify(this.secrets)) : '';
  }

  private getDecodedAccessToken(): DecodedToken | null {
    if (this.secrets.accessToken) {
      try {
        const { id_token } = this.secrets.accessToken;
        if (!id_token) {
          console.warn('No token to decode.');
          return null;
        }
        let decodedToken: DecodedToken;
        if (windowExists()) {
          decodedToken = JSON.parse(atob(id_token.split('.')[1]));
        } else {
          // For decoding the token on the server
          decodedToken = JSON.parse(Buffer.from(id_token.split('.')[1], 'base64').toString('ascii'));
        }
        return decodedToken;
      } catch (e) {
        console.error(`Unable to decode token.`);
        console.error(e);
        return null;
      }
    }
    return null;
  }

  private getAccessToken(): string | null {
    if (this.secrets.accessToken) {
      // return this.isTokenExpired() ? null : this.secrets.accessToken.id_token;
      // Agreeing to send the token even if it's expired as we try to find out why the token is not refreshing
      return this.secrets.accessToken.id_token;
    } else {
      return null;
    }
  }

  private getTimeLeftInSecondsBeforeExpiration(decodedToken: DecodedToken, offset: number = 0): number {
    // The following logic is extracted from our current JWT module
    return decodedToken.exp - Math.floor((Date.now() - offset) / 1000);
  }

  // private isTokenExpired(): boolean {
  //   const decodedToken = this.getDecodedAccessToken();
  //   // Slightly inaccurate to say it's expired when the token doesn't exist
  //   return decodedToken ? this.getTimeLeftInSecondsBeforeExpiration(decodedToken) <= 0 : true;
  // }

  /**
   * Parses auth cookie string for SSR usage.
   *
   * **Cookie Format Evolution:**
   * - Legacy: Contains only AccessToken (for SSR compatibility)
   * - Current: Transitioning to full Secrets object
   * - Future: Will always contain full Secrets object
   *
   * **SSR Considerations:**
   * - Extracts AccessToken for authentication header
   * - Calculates TTL to validate token freshness
   * - Uses same threshold as background token refresh (BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS)
   * - Prevents hydration mismatches where server renders auth content but client sees expired token
   *
   * @param stringifiedToken - URL-encoded JSON string from cookie (may be AccessToken or full Secrets)
   * @param options - Configuration options for parsing
   * @param options.minTtlSeconds - Minimum TTL in seconds for token to be considered valid (default: 1000s = ~16.67 min)
   * @returns Parsed token and TTL if valid, or undefined if invalid/missing/expired
   */
  public static parseAuthCookie(stringifiedToken: string | null | undefined, options?: { minTtlSeconds?: number }): { token: AccessToken; ttl: number } | undefined {
    if (!stringifiedToken) {
      return;
    }

    const minTtlSeconds = options?.minTtlSeconds ?? BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS;

    let token: AccessToken | undefined = undefined;
    let ttl: number;

    try {
      const parsed = JSON.parse(stringifiedToken);

      // Handle both formats: AccessToken directly or Secrets object with accessToken property
      if ((parsed.token_type || parsed.bearer_type) && parsed.id_token) {
        // Legacy format: just AccessToken (Auth0 uses token_type, app uses bearer_type)
        token = parsed as AccessToken;
      } else if (parsed.accessToken) {
        // New format: full Secrets object
        token = typeof parsed.accessToken === 'string' ? JSON.parse(parsed.accessToken) : parsed.accessToken;
      } else {
        console.error('Invalid auth cookie format: missing token data');
        return;
      }

      if (!token || !token.id_token) {
        console.error('Invalid token: missing id_token');
        return;
      }

      const decodedToken = windowExists()
        ? JSON.parse(atob(token.id_token.split('.')[1]))
        : // For decoding the token on the server (node environment)
          JSON.parse(Buffer.from(token.id_token.split('.')[1], 'base64').toString('ascii'));

      ttl = Math.floor(decodedToken.exp - Date.now() / 1000);

      // Reject tokens that expire too soon to prevent SSR/client hydration mismatches
      if (ttl < minTtlSeconds) {
        console.warn(`Auth token TTL (${ttl}s) is less than minimum required (${minTtlSeconds}s) for SSR`);
        return;
      }
    } catch (error) {
      console.error('Malformed SSR auth token', error);
      return;
    }

    return { token, ttl };
  }

  private parseSecretsStringToJSON(secretsString: string): Secrets {
    try {
      const parsed = JSON.parse(secretsString);

      // Check if this is a cookie format (just AccessToken) vs localStorage format (full Secrets)
      if ((parsed.token_type || parsed.bearer_type) && parsed.id_token) {
        // Cookie format: just the AccessToken object (Auth0 uses token_type, app uses bearer_type)
        return { accessToken: parsed };
      }

      // localStorage format: full Secrets object
      const secrets = parsed;

      // we used to stringify access token itself so need to handle that case for backward compat
      // probably can remove this code after July 2020 (3 months from now)
      let accessToken = secrets.accessToken;
      if (typeof secrets.accessToken === 'string') {
        accessToken = JSON.parse(accessToken);
      }

      accessToken = accessToken || null;

      return { ...secrets, accessToken };
    } catch (e) {
      return {};
    }
  }

  private writeToSecretsAndAlertListeners(newToken: AccessToken | null, clientId?: string | null, refreshToken?: string, serverTime?: number): void {
    this.secrets.accessToken = newToken || undefined;

    if (refreshToken) {
      this.secrets.refreshToken = refreshToken;
    }

    if (clientId) {
      this.secrets.accessTokenClientId = clientId;
      this.secrets.refreshTokenClientId = clientId;
    }

    if (serverTime) {
      this.secrets.offset = Date.now() - serverTime;
    }

    const serializedSecrets = this.prepareSecretsForWrite();

    // Write to primary storage (cookies)
    this.setSecretsToStorage(serializedSecrets);

    // Sync to localStorage for backwards compatibility
    // TODO: Remove after migration confidence (1-2 months post-deployment)
    if (this.localStorage) {
      this.localStorage.setItem('secrets', serializedSecrets);
    }

    this.broadcastTokenUpdate(newToken);
  }

  private prepareSecretsForWrite = (): string => {
    const { accessToken, ...restSecrets } = this.secrets;

    const withStringifiedAccessToken: PersistedSecrets = {
      ...restSecrets,
    };

    try {
      // All apps expect `accessToken` to be stringified when reading from local storage
      withStringifiedAccessToken.accessToken = JSON.stringify(accessToken);
    } catch (e) {
      this.logger.ensureSecretsFormatError(e);
    }
    return JSON.stringify(withStringifiedAccessToken);
  };

  /**
   * Returns the storage key based on the storage type.
   * Cookies use 'SsrAuthToken' (with 'joy' prefix → 'joySsrAuthToken') for SSR compatibility.
   * localStorage uses 'secrets' for backwards compatibility.
   */
  private getStorageKey(): string {
    return this.storage instanceof CookieStorage ? SSR_TOKEN_KEY : LOCAL_STORAGE_TOKEN_KEY;
  }

  /**
   * Reads auth secrets from primary storage (cookies or localStorage fallback).
   * Uses storage-specific key: 'joySsrAuthToken' for cookies, 'secrets' for localStorage.
   */
  private getSecretsFromStorage(): string | null | undefined {
    const key = this.getStorageKey();
    return this.storage.getItem(key);
  }

  /**
   * Writes auth secrets to primary storage (cookies or localStorage fallback).
   *
   * **Storage Format:**
   * - Cookies: stores FULL Secrets object (for cookie-primary future)
   * - localStorage: stores FULL Secrets object
   *
   * **Rationale:** Cookies must contain complete secrets (refreshToken, clientIds, offset)
   * to function as primary storage. Storing only AccessToken would break token refresh.
   *
   * Uses storage-specific key: 'joySsrAuthToken' for cookies, 'secrets' for localStorage.
   * Note: Call sites should explicitly sync to localStorage for backwards compatibility.
   */
  private setSecretsToStorage(value: string): void {
    const key = this.getStorageKey();
    this.storage.setItem(key, value);
  }

  /**
   * Deletes auth secrets from primary storage (cookies or localStorage fallback).
   * Uses storage-specific key: 'joySsrAuthToken' for cookies, 'secrets' for localStorage.
   * Note: Call sites should explicitly sync deletion to localStorage for backwards compatibility.
   */
  private deleteSecretsFromStorage(): void {
    const key = this.getStorageKey();
    this.storage.setItem(key, undefined);
  }
}
